import { Component } from 'react';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(err) {
    return { error: err };
  }

  componentDidCatch(err, info) {
    console.error('[ErrorBoundary]', err, info?.componentStack);
    this.setState({ info });
  }

  // Short diagnostic text (shown on screen + copyable) so errors seen on a
  // phone / in the Android app can be traced back to the source.
  details() {
    const e = this.state.error;
    const stack = String(e?.stack || '').split('\n').slice(0, 6).join('\n');
    const comp = String(this.state.info?.componentStack || '').trim().split('\n').slice(0, 4).join('\n');
    return [
      `${e?.name || 'Error'}: ${e?.message || e}`,
      `at ${window.location.pathname}${window.location.search} · v${process.env.REACT_APP_VERSION || '?'}`,
      `UA: ${navigator.userAgent}`,
      stack, comp,
    ].filter(Boolean).join('\n');
  }

  render() {
    if (!this.state.error) return this.props.children;

    const isChunkError = this.state.error?.name === 'ChunkLoadError'
      || this.state.error?.message?.includes('Loading chunk')
      || this.state.error?.message?.includes('dynamically imported module');

    return (
      <div style={{
        minHeight: '100vh', background: '#070c1a', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 24, textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>🛰️</div>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace", fontSize: 14,
          color: '#65d4fa', letterSpacing: 2, marginBottom: 12,
        }}>
          {isChunkError ? 'UPDATE REQUIRED' : 'SYSTEM ERROR'}
        </div>
        <div style={{
          fontFamily: "'Inter', sans-serif", fontSize: 13,
          color: '#8892a4', maxWidth: 320, lineHeight: 1.7, marginBottom: 28,
        }}>
          {isChunkError
            ? 'A newer version of the app is available. Reload to update.'
            : 'Something went wrong. Please reload the page.'}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            fontFamily: "'Share Tech Mono', monospace", fontSize: 11,
            letterSpacing: 1, padding: '12px 28px', cursor: 'pointer',
            background: 'rgba(101,212,250,0.12)',
            border: '1px solid rgba(101,212,250,0.4)',
            color: '#65d4fa', borderRadius: 4,
          }}>
          RELOAD APP
        </button>
        {!isChunkError && (
          <div style={{
            marginTop: 16, fontFamily: 'monospace', fontSize: 9,
            color: '#4d5a6e', maxWidth: 340, wordBreak: 'break-all',
          }}>
            {this.state.error?.message}
            <pre style={{ textAlign:'left', whiteSpace:'pre-wrap', marginTop:12, maxHeight:180, overflow:'auto',
              fontSize:8, color:'#4d5a6e', background:'#0b1224', padding:8, borderRadius:4 }}>
              {this.details()}
            </pre>
            <button
              onClick={() => {
                const text = this.details();
                const done = () => this.setState({ copied: true });
                try { navigator.clipboard.writeText(text).then(done, () => {}); } catch (_) {}
              }}
              style={{ marginTop:8, fontFamily:'monospace', fontSize:9, letterSpacing:1, padding:'6px 14px',
                cursor:'pointer', background:'transparent', border:'1px solid #4d5a6e', color:'#8892a4', borderRadius:4 }}>
              {this.state.copied ? '✓ COPIED' : 'COPY DETAILS'}
            </button>
          </div>
        )}
      </div>
    );
  }
}
